import { useEffect, useRef, useState } from 'react';
import { useApiHolder, type SignInPageProps } from '@backstage/core-plugin-api';
import {
  UserIdentity,
  type SignInProviderConfig,
} from '@backstage/core-components';

/**
 * Storage key the built-in Backstage `SignInPage` uses to remember the
 * last-used provider. We deliberately reuse the exact same key so the chosen
 * provider is shared with (and survives a switch to/from) the default sign-in
 * page and the single-provider `auto` branch.
 *
 * This is an internal constant of `@backstage/core-components` and is not
 * exported, so it's mirrored here. The accompanying test locks the contract;
 * if a Backstage upgrade ever changes it, the test is the place that flags it.
 */
export const PROVIDER_STORAGE_KEY = '@backstage/core:SignInPage:provider';

/** Records the provider the user chose so the next visit can silently restore it. */
export function rememberSignInProvider(id: string): void {
  try {
    localStorage.setItem(PROVIDER_STORAGE_KEY, id);
  } catch {
    return;
  }
}

/** Clears the remembered provider (on sign-in failure or a failed restore). */
export function forgetSignInProvider(): void {
  try {
    localStorage.removeItem(PROVIDER_STORAGE_KEY);
  } catch {
    return;
  }
}

function getRememberedSignInProviderId(): string | null {
  try {
    return localStorage.getItem(PROVIDER_STORAGE_KEY);
  } catch {
    return null;
  }
}

export type UseSilentSignInRestoreOptions = {
  providers: SignInProviderConfig[];
  onSignInSuccess: SignInPageProps['onSignInSuccess'];
  silentSignInProviderId?: string;
};

/**
 * Reinstates the silent session restore that Backstage's built-in `SignInPage`
 * performs internally (via its non-exported `useSignInProviders` hook), so a
 * custom sign-in layout can keep the "session is remembered across visits"
 * behavior.
 *
 * On mount it looks up the last-used provider and attempts a popup-free
 * `getBackstageIdentity({ optional: true })`. A caller can also provide a
 * `silentSignInProviderId` for an auth implementation, such as the local GitHub
 * CLI provider, that can establish a first session through its refresh endpoint
 * without interactive OAuth. If authentication succeeds, the hook signs the
 * user straight in; otherwise it stops restoring so the caller can render its
 * provider picker.
 *
 * @returns `restoring` — `true` while the silent restore is in flight (render a
 * loading state and not the provider picker to avoid a flash of the sign-in UI).
 */
export function useSilentSignInRestore({
  providers,
  onSignInSuccess,
  silentSignInProviderId,
}: UseSilentSignInRestoreOptions): boolean {
  const apiHolder = useApiHolder();
  // Start in a restoring state so callers don't flash the sign-in UI before we
  // know whether an existing session can be silently restored.
  const [restoring, setRestoring] = useState(true);

  // The restore is a one-shot mount concern, so the effect must run exactly
  // once and never re-fire the silent getBackstageIdentity. We read every
  // dependency through a ref instead of listing it in the dependency array:
  // `providers` is frequently an inline array and `onSignInSuccess` an inline
  // callback, and even `apiHolder` can change identity in some hosts (e.g. test
  // harnesses), any of which would otherwise re-trigger the effect.
  const apiHolderRef = useRef(apiHolder);
  apiHolderRef.current = apiHolder;
  const providersRef = useRef(providers);
  providersRef.current = providers;
  const onSignInSuccessRef = useRef(onSignInSuccess);
  onSignInSuccessRef.current = onSignInSuccess;
  const silentSignInProviderIdRef = useRef(silentSignInProviderId);
  silentSignInProviderIdRef.current = silentSignInProviderId;

  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      const storedId = getRememberedSignInProviderId();
      const storedProvider = storedId
        ? providersRef.current.find(candidate => candidate.id === storedId)
        : undefined;
      const provider =
        storedProvider ??
        providersRef.current.find(
          candidate => candidate.id === silentSignInProviderIdRef.current,
        );

      if (!provider) {
        if (!cancelled) {
          setRestoring(false);
        }
        return;
      }

      try {
        const authApi = apiHolderRef.current.get(provider.apiRef);
        const identityResponse = await authApi?.getBackstageIdentity({
          optional: true,
        });
        if (cancelled) {
          return;
        }
        if (authApi && identityResponse) {
          const profile = await authApi.getProfile();
          if (cancelled) {
            return;
          }
          rememberSignInProvider(provider.id);
          onSignInSuccessRef.current(
            UserIdentity.create({
              identity: identityResponse.identity,
              authApi,
              profile,
            }),
          );
          return;
        }
        forgetSignInProvider();
        setRestoring(false);
      } catch {
        if (cancelled) {
          return;
        }
        forgetSignInProvider();
        setRestoring(false);
      }
    };

    void restore();

    return () => {
      cancelled = true;
    };
    // Mount-only: all inputs are read through refs above.
  }, []);

  return restoring;
}
